import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/lead-battery-conference-detail.webp";
import HeroImgMobile       from "../../assets/images/blogs/lead-battery-conference-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";

/* ─────────────────────────────────────────────
   TheEuropeanLeadBatteryConferenceAndExhibitionIsLiveAndOnline.jsx
   Prefix      : lb-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   DIFF vs other pages:
   - Hero title  : CENTERED (2 lines)
   - lb-bullet--bold  : • bold bullet item
   - lb-label-para    : bold label + dash + normal text (no bullet)
   - lb-bullet        : • regular bullet item
   - lb-para--italic  : italic closing para
   - No subheads, no images in body
   - Newsletter  : prop-based isMobile, Subscribe button
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .lb-wrap *, .lb-wrap *::before, .lb-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .lb-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .lb-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .lb-content-col { width: 100%; }

  /* ── SCROLL FADE-UP ── */
  .lb-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .lb-fade-up.lb-in { opacity: 1; transform: translateY(0); }
  .lb-delay-1 { transition-delay: 0.08s; }
  .lb-delay-2 { transition-delay: 0.18s; }
  .lb-delay-3 { transition-delay: 0.28s; }
  .lb-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO — centered title
  ══════════════════════════════════════ */
  .lb-hero { width: 100%; background: #1A1A1A; }
  .lb-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .lb-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: center;
  }
  .lb-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .lb-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .lb-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 100px;
  }

  /* regular paragraph */
  .lb-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  /* italic closing paragraph */
  .lb-para--italic {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    font-style: italic;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  /* bullet list wrapper */
  .lb-list {
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* • bold bullet — "A full programme..." */
  .lb-bullet--bold {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 160%;
    color: #ffffff;
    margin-bottom: 14px;
  }
  .lb-bullet--bold::before {
    content: '•';
    flex-shrink: 0;
    font-size: 18px;
    line-height: 160%;
    color: #ffffff;
  }

  /* bold label + dash + normal text — "Industry Insights –..." */
  .lb-label-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 14px;
  }
    .lb-para--italic:last-child { margin-bottom: 0px;}
  .lb-label-para strong {
    font-weight: 700;
    color: #ffffff;
  }

  /* • regular bullet — "The ELBC Exhibition..." */
  .lb-bullet {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 16px;
   
  }
  .lb-bullet::before {
    content: '•';
    flex-shrink: 0;
    font-size: 18px;
    line-height: 160%;
    color: #E2E2E2;
  }

  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .lb-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .lb-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    
    object-position: center;
    z-index: 0;
  }
  .lb-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .lb-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .lb-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .lb-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .lb-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .lb-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .lb-newsletter__input::placeholder { color: #FFFFFF; }
  .lb-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .lb-newsletter__btn:hover { background: #5e2e90; }
  .lb-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .lb-newsletter__btn:hover .lb-newsletter__btn-arrow { transform: translateX(4px); }
  .lb-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .lb-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .lb-inner { padding-left: 32px; padding-right: 32px; }
    .lb-hero__img-wrap img { max-height: 350px; }
    .lb-hero__title-bar { padding: 24px 0px 20px; }
    .lb-hero__title { font-size: 24px; }
    .lb-body-section { padding: 40px 0 48px; }
    .lb-para { margin-bottom: 18px; font-size: 15px; }
    .lb-para--italic { margin-bottom: 18px; font-size: 15px; }
    .lb-bullet--bold { font-size: 15px; }
    .lb-label-para { font-size: 15px; }
    .lb-bullet { font-size: 15px; }

    .lb-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .lb-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .lb-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .lb-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .lb-newsletter__heading { font-size: 24px; }
    .lb-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .lb-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .lb-newsletter__input { border-radius: 5px; height: 44px; }
    .lb-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .lb-inner { padding-left: 16px; padding-right: 16px; }
    .lb-hero__title-bar { padding: 30px 0px; }
    .lb-body-section { padding: 30px 0 30px; }
    .lb-hero__img-wrap img { max-height: 366px; width: 100%; }
    .lb-hero__title { font-size: 26px; line-height: 120%; }
    .lb-para { font-size: 14px; margin-bottom: 14px; }
    .lb-para--italic { font-size: 14px; margin-bottom: 14px; }
    .lb-bullet--bold { font-size: 18px; line-height:140%; }
    .lb-label-para { font-size: 14px; }
    .lb-bullet { font-size: 14px; }

    .lb-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .lb-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .lb-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .lb-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .lb-newsletter__heading { font-size: 30px; line-height: 120%; }
    .lb-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .lb-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .lb-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .lb-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".lb-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("lb-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="lb-newsletter">
      {!isMobile && (
        <>
          <img className="lb-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="lb-newsletter__overlay" />
        </>
      )}
      <div className="lb-newsletter__inner">
        {isMobile && (
          <img className="lb-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="lb-newsletter__content">
          <h2 className="lb-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="lb-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="lb-newsletter__form">
            <input
              className="lb-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="lb-newsletter__btn">
              Subscribe
              <span className="lb-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function TheEuropeanLeadBatteryConferenceAndExhibitionIsLiveAndOnline() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="lb-wrap">

        {/* HERO — centered title */}
        <section className="lb-hero">
          <div className="lb-hero__title-bar">
            <div className="lb-inner">
              <h1 className="lb-hero__title">The European Lead Battery Conference &amp; Exhibition is live and online 22-24 September</h1>
            </div>
          </div>
          <div className="lb-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="European Lead Battery Conference 17ELBC"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="lb-body-section">
          <div className="lb-inner">
            <div className="lb-content-col">

              {/* opening para — image 2 */}
              <p className="lb-para lb-fade-up lb-delay-1">At the first global lead battery gathering since the coronavirus pandemic, we're bringing you updates over three days from the ELBC Studio, including:</p>

              {/* bold bullet — image 2 */}
              <p className="lb-bullet--bold lb-fade-up lb-delay-1">
                A full programme of insights and analysis from over 50 leading industry figures and research experts:
              </p>

              {/* label paras — image 2 */}
              <p className="lb-label-para lb-fade-up lb-delay-1">
                <strong>Industry Insights</strong> — leading experts and company executives share their analysis and debate the impact of the pandemic on the lead battery industry
              </p>

              <p className="lb-label-para lb-fade-up lb-delay-1">
                <strong>Technical Sessions</strong> — the latest lead battery research and innovation, from fundamental science, through to automotive and energy storage applications will be presented and debated in a new format over six themed panel sessions
              </p>

              {/* regular bullet — image 2 */}
              <p className="lb-bullet lb-fade-up lb-delay-1">
                The ELBC Exhibition will be open throughout the event — showcasing the most exciting products and innovations from around the world and providing unique networking opportunities for all delegates in the live Networking Lounge
              </p>

              {/* italic closing para — image 2 */}
              <p className="lb-para--italic lb-fade-up lb-delay-1">
                Full access to all video presentations on-demand alongside live discussion and debate means you won't miss out on any of the ELBC action.
              </p>

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}